import { useCallback, useEffect, useState } from "react";
import axios from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import cloudinaryConfig from "../../config/cloudinary.config";
import imageCompression from "browser-image-compression";
import { useTranslation } from "react-i18next";
import {
  AddImageButton,
  CancelButton,
  DeleteButton,
  EditImageButton,
  SaveButton,
} from "../buttons/Buttons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { CSS } from "@dnd-kit/utilities";

const WEBSITE_URL = "/image_url";

function SortableImage({ image, handleDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: image.public_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="gallery-image-container"
    >
      <img
        className="gallery-image"
        src={image.url}
        alt="gallery"
        {...attributes}
        {...listeners} 
        style={{ cursor: "grab" }}
      />
      <DeleteButton
        buttonText="Delete"
        onClick={() => handleDelete(image.public_id)}
        type="button"
      />
    </div>
  );
}


export default function CarouselContent(props) {
  const galleryTitle = "gallery-" + props.title;
  const [images, setImages] = useState([]);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [lastIndex, setLastIndex] = useState(0);
  const [imageObjects, setImageObjects] = useState([]);
  const [overwrite] = useState(false);
  const [status, setStatus] = useState("");

  const maxImages = props.maxImages;
  const [uploadLegal, setUploadLegal] = useState(false);
  const [nofImagesInDB, setNofImagesInDB] = useState(-1);
  const [nofImagesToUpload, setNofImagesToUpload] = useState(0);

  const { username } = useAuth();
  const { t } = useTranslation();

  const updateGallery = useCallback(async () => {
    const url = WEBSITE_URL + "/gallery";
    // console.log("Fetching gallery:", { title: galleryTitle, username });
  
    try {
      const response = await axios.get(url, {
        params: { title: galleryTitle, username },
      });
  
    //   console.log("Raw response:", response.data);
  
      if (response.data && response.data.length > 0) {
        const images = response.data
          .map((item, idx) => ({
            url: item.url,
            public_id: item.public_id,
            sortOrder: item.sortOrder ?? idx, 
          }))
          .sort((a, b) => a.sortOrder - b.sortOrder); 
  
        // console.log("Images after applying sortOrder:", images);
  
        const lastIndex = Math.max(
          ...images.map((obj) => {
            const parts = obj.public_id.split("-");
            return parseInt(parts[parts.length - 1], 10) || 0;
          })
        );
  
        setLastIndex(lastIndex);
        setNofImagesInDB(images.length);
        setImageObjects(images);
      } else {
        console.log("No images found.");
      }
    } catch (err) {
      console.error("Failed to fetch gallery:", err);
    }
  }, [galleryTitle, username]);
  

  useEffect(() => {
    const update = async () => {
      await updateGallery(lastIndex, images, status);
    };
    update();
  }, [lastIndex, images, status, updateGallery]);

  useEffect(() => {
    const update = async () => {
      await updateGallery(lastIndex, images, status);
    };
    update();
  }, [updateGallery, images, lastIndex, status]);

  useEffect(() => {
    const uploadLegal = nofImagesInDB + nofImagesToUpload <= maxImages;
    setUploadLegal(uploadLegal);
    if (!uploadLegal) {
      setStatus(
        t("warnings.max_images_1.1") +
          String(maxImages) +
          t("warnings.max_images_1.2") +
          String(maxImages - nofImagesInDB) +
          t("warnings.max_images_1.3")
      );
    } else if (nofImagesInDB === maxImages) {
      setStatus(
        t("warnings.max_images_2.1") +
          String(maxImages) +
          t("warnings.max_images_2.2")
      );
    } else {
      setStatus(t("hints.image_upload"));
    }
  }, [setUploadLegal, maxImages, nofImagesInDB, nofImagesToUpload, t]);

  async function handleFileUpload(event) {
    const newImages = [...images];
    const files = event.target.files;
    const maxSizeMB = 0.3;
    const maxWidthOrHeight = 1920;

    const options = { maxSizeMB, maxWidthOrHeight };

    for (let file of files) {
      const reader = new FileReader();
      try {
        const compressedFile = await imageCompression(file, options);
        reader.readAsDataURL(compressedFile);
        reader.onload = () => {
          newImages.push(reader.result);
          setImages(newImages);
        };
      } catch (error) {
        reader.readAsDataURL(file);
        reader.onload = () => {
          newImages.push(reader.result);
          setImages(newImages);
        };
      }
    }
    setNofImagesToUpload(files.length);
  }

  const handleDelete = async (public_id) => {
  try {
    setStatus("Deleting image...");
    const signResponse = await axios.get("/signuploadform", {
      params: { publicID: public_id },
    });

    const signData = signResponse.data;
    const url =
      cloudinaryConfig.BASE_URL +
      signData.cloudName +
      cloudinaryConfig.DELETE_SUFFIX;

    if (signData.signature && url) {
      const cloudDelete = await axios.post(url, {
        signature: signData.signature,
        api_key: signData.apiKey,
        cloud_name: signData.cloudName,
        timestamp: signData.timestamp,
        public_id,
        invalid: true,
      });

      console.log("Cloudinary delete result:", cloudDelete.data);

      const dbDelete = await axios.delete(WEBSITE_URL, {
        data: { username, title: galleryTitle, public_id },
      });

      console.log("DB delete result:", dbDelete.data);
    } else {
      console.warn("Missing signature or URL for delete");
    }

    await updateGallery();
    setStatus("Image deleted successfully.");
  } catch (error) {
    console.error("Delete failed:", error);
    setStatus("Failed to delete image.");
  }
};


  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("Uploading...");
    const folder = `${props.username}/${galleryTitle}`;

    if (images.length < 1) return setStatus(t("hints.min_one_image"));

    try {
      const uploadPromises = images.map(async (image, index) => {
        const newIndex = index + Number(!overwrite) * lastIndex + 1;
        const signResponse = await axios.get("/signuploadform", {
          params: { folder, publicID: `${props.title}-${newIndex}` },
        });
        const signData = signResponse.data;
        const url =
          cloudinaryConfig.BASE_URL +
          signData.cloudName +
          cloudinaryConfig.UPLOAD_SUFFIX;

        const formData = new FormData();
        formData.append("api_key", signData.apiKey);
        formData.append("cloud_name", signData.cloudName);
        formData.append("file", image);
        formData.append("folder", folder);
        formData.append("public_id", `${props.title}-${newIndex}`);
        formData.append("signature", signData.signature);
        formData.append("timestamp", signData.timestamp);

        const uploadResponse = await axios.post(url, formData);
        await axios.put("/image_url", {
          username: props.username,
          title: galleryTitle,
          url: uploadResponse.data.secure_url,
          public_id: uploadResponse.data.public_id,
        });
      });
      await Promise.all(uploadPromises);
      setStatus(t("success.upload"));
      await delay(1000);
      setImages([]);
      await updateGallery();
      setAdding(false);
    } catch (error) {
      console.error(error);
      setStatus("Error occurred during upload.");
    }
  };

  const handleOuterCancel = () => {
    setAdding(false);
    setEditing(false);
    setStatus("");
  };

  const handleInnerCancel = () => {
    setAdding(false);
    setStatus("");
    setNofImagesToUpload(0);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
  
    console.log("Drag end triggered");
    console.log("Active ID:", active.id);
    console.log("Over ID:", over?.id);
  
    if (active.id !== over?.id) {
      const oldIndex = imageObjects.findIndex(
        (img) => img.public_id === active.id
      );
      const newIndex = imageObjects.findIndex(
        (img) => img.public_id === over.id
      );
  
      console.log("Old Index:", oldIndex);
      console.log("New Index:", newIndex);
  
      const newOrder = arrayMove(imageObjects, oldIndex, newIndex);
      console.log("New Order Array:", newOrder.map(i => i.public_id));
  
      setImageObjects(newOrder);
  
      try {
        const payload = {
          username,
          title: galleryTitle,
          order: newOrder.map((img, index) => ({
            public_id: img.public_id,
            sortOrder: index,
          })),
        };
  
        console.log("Sending reorder payload:", payload);
  
        const response = await axios.post(`${WEBSITE_URL}/reorder`, payload);
  
        console.log("Reorder response:", response.data);
      } catch (error) {
        console.error("Failed to save image order:", error);
      }
    }
  };
  

  return (
    <div className="container container-carousel">
      {((images.length === 0 && nofImagesInDB === 0) ||
        nofImagesInDB === -1) && (
        <div className="hint-msg">{t("hints.no_images_in_gallery")}</div>
      )}
      {editing ? (
        <div>
          {!adding && (
            <div>
              <AddImageButton
                buttonText={t("button.add")}
                disable={!uploadLegal || nofImagesInDB >= maxImages}
                onClick={() => {
                  setAdding(true);
                  setStatus("Please choose a file to upload!");
                }}
              />
              <CancelButton
                buttonText={t("button.cancel")}
                onClick={handleOuterCancel}
              />
            </div>
          )}
          {adding && (
            <form onSubmit={handleSubmit}>
              <span className="img-upload-form">
                <input type="file" multiple onChange={handleFileUpload} />
              </span>
              <SaveButton
                buttonText={t("button.upload")}
                enabled={uploadLegal}
                type="submit"
              />
              <CancelButton
                buttonText={t("button.cancel")}
                onClick={handleInnerCancel}
              />
              {status && <span className="status-info">{status}</span>}
            </form>
          )}
          {nofImagesInDB >= maxImages && uploadLegal && (
            <span className="status-info">{status}</span>
          )}
        </div>
      ) : (
        <div>
          <EditImageButton
            buttonText={t("button.edit_gallery")}
            onClick={() => setEditing(true)}
          />
        </div>
      )}

      {imageObjects.length > 0 &&
        (editing ? (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={imageObjects.map((i) => i.public_id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="sortable-grid-wrapper">
                {imageObjects.map((image) => (
                  <SortableImage
                    key={image.public_id}
                    image={image}
                    handleDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={16}
            slidesPerView={1}
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            loop
          >
            {imageObjects.map((img, idx) => (
              <SwiperSlide key={idx}>
                <div className="gallery-carousel-wrapper">
                  <img
                    className="image-full"
                    src={img.url}
                    alt={`Slide-${idx}`}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ))}
    </div>
  );
}
